import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Countdown } from "@/components/Countdown";
import { useApi, fmtIST, MODE_LABEL, FAMILY_LABEL } from "@/lib/useApi";
import { ArrowRight, Zap, FileCheck2 } from "lucide-react";

export default function HomePage() {
  const { me } = useOutletContext();
  const api = useApi();
  const [elig, setElig] = useState(null);
  const [ann, setAnn] = useState(null);
  const [prog, setProg] = useState(null);

  useEffect(() => {
    api.get("/exams/eligibility").then((r) => setElig(r.data));
    api.get("/announcements").then((r) => setAnn(r.data));
    api.get("/progress").then((r) => setProg(r.data));
  }, [api]);

  const offset = ann ? Date.parse(ann.server_now) - Date.now() : 0;
  const recent = prog ? [...prog.trend].reverse().slice(0, 5) : [];
  const avg = prog?.trend.length ? Math.round(prog.trend.reduce((s, t) => s + t.percent, 0) / prog.trend.length) : null;
  const sub = elig?.subscription;

  return (
    <div className="space-y-8 max-w-6xl" data-testid="student-dashboard">
      <div>
        <h1 className="font-display font-bold text-3xl text-slate-900 tracking-tight">Welcome back{me?.first_name ? `, ${me.first_name}` : ""}.</h1>
        <div className="mt-3 flex flex-wrap gap-2">
          {(me?.selected_families || []).map((f) => <Badge key={f} variant="outline" className="border-slate-200">{FAMILY_LABEL[f]}</Badge>)}
          <Link to="/app/my-subjects" className="text-sm text-blue-600 hover:underline">{me?.selected_subjects?.length || 0} subject(s) · edit</Link>
        </div>
      </div>

      {elig?.in_progress_attempt_id && (
        <Card className="p-5 border-blue-200 bg-blue-50 flex items-center justify-between gap-3" data-testid="dashboard-resume">
          <p className="text-sm text-blue-900">You have an exam in progress. The timer keeps running on the server.</p>
          <Button asChild size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"><Link to={`/exam/${elig.in_progress_attempt_id}`}>Resume</Link></Button>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <Card className="p-6 border-slate-200" data-testid="dashboard-free-attempts">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Free attempts left</p>
          <p className="font-display font-extrabold text-4xl mt-2">{elig?.free_attempts_remaining ?? "—"}<span className="text-lg text-slate-400"> / 3</span></p>
          {sub?.active && <p className="text-xs text-slate-500 mt-1">Not used while your subscription is active.</p>}
        </Card>
        <Card className="p-6 border-slate-200" data-testid="dashboard-subscription">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Subscription</p>
          {sub?.active ? <><p className="font-display font-bold text-xl mt-2 text-emerald-700">Active</p>
            <p className="text-sm text-slate-700 mt-1" data-testid="dashboard-allowance">{sub.exams_used} of {sub.exam_allowance} exams used — {sub.exams_remaining} remaining</p>
            <p className="text-xs text-slate-500 mt-1">until {fmtIST(sub.expires_at)}</p></>
            : <><p className="font-display font-bold text-xl mt-2 text-slate-900">Not subscribed</p><Link to="/app/subscription" className="text-xs text-blue-600">View plans</Link></>}
        </Card>
        <Card className="p-6 border-slate-200" data-testid="dashboard-average">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-medium">Average score</p>
          <p className="font-display font-extrabold text-4xl mt-2">{avg === null ? "—" : `${avg}%`}</p>
          <p className="text-xs text-slate-500 mt-1">{prog?.attempts || 0} submitted attempt(s)</p>
        </Card>
      </div>

      <div>
        <h2 className="font-display font-semibold text-lg mb-3">Official exam countdown</h2>
        <div className="grid md:grid-cols-2 gap-4">{(ann?.items || []).map((it) => <Countdown key={it.family} item={it} offset={offset} />)}</div>
      </div>

      <Card className="p-6 border-slate-200">
        <h2 className="font-display font-semibold text-lg">Start a session</h2>
        {elig && !elig.can_start && !elig.in_progress_attempt_id && <p className="text-sm text-red-600 mt-2" data-testid="dashboard-ineligible">{elig.reason}</p>}
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <ModeLink to="/app/practice" icon={Zap} title="Practice" body="Quick (15 min) or Intermediate (90 min) — scaled from the official pattern." disabled={elig && !elig.can_start} testid="dashboard-practice-btn" />
          <ModeLink to="/app/mock-tests" icon={FileCheck2} title="Full-length mock" body="Exact official blueprint, available once the blueprint is verified." disabled={elig && !elig.can_start} testid="dashboard-mock-btn" />
        </div>
      </Card>

      <Card className="p-6 border-slate-200" data-testid="dashboard-recent">
        <div className="flex items-center justify-between"><h2 className="font-display font-semibold text-lg">Recent results</h2><Link to="/app/results" className="text-sm text-blue-600">All progress</Link></div>
        {recent.length ? <div className="mt-3 divide-y divide-slate-100">
          {recent.map((t) => (
            <Link key={t.id} to={`/app/results/${t.id}`} className="flex justify-between py-3 text-sm hover:bg-slate-50 px-2 rounded">
              <span>{MODE_LABEL[t.mode]} · {FAMILY_LABEL[t.family]} <span className="text-slate-400">· {fmtIST(t.at)}</span></span><span className="font-mono">{t.score}/{t.max_score}</span>
            </Link>))}
        </div> : <p className="text-sm text-slate-500 mt-3">No results yet.</p>}
      </Card>
    </div>
  );
}

function ModeLink({ to, icon: Icon, title, body, disabled, testid }) {
  return (
    <Link to={to} data-testid={testid} className={`group p-5 rounded-xl border border-slate-200 hover:border-blue-300 transition-colors ${disabled ? "opacity-60" : ""}`}>
      <Icon className="h-5 w-5 text-blue-600" />
      <p className="font-display font-semibold mt-3 flex items-center gap-1">{title}<ArrowRight className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" /></p>
      <p className="text-sm text-slate-600 mt-1">{body}</p>
    </Link>
  );
}
